import { useState } from 'react';
import type { RewardConfig, RewardLevelRowView, RewardSnapshot } from '@contracts';
import { RewardIcon } from '@ui/shared/icons/RewardIcon';
import { IconPickerPopover } from './IconPickerPopover';

/**
 * `LevelArcList` (settings-rewards.md §1.2, §8 component inventory) — the
 * named ten-level arc rendered as a vertical list, one row per level: level
 * number, icon tile (neutral for every row except current), name, XP
 * threshold right-aligned. Current level's row gets the vivid "you are
 * here" treatment (surface.hover background, border.strong outline, icon
 * tile inverts to hue.primary.vivid fill with text.onAccent icon — the ONE
 * place on this screen the brand hue marks "you").
 *
 * Edit mode (§1.3): threshold/name/icon become editable per row; `onChange`
 * receives the full next `RewardConfig` on every commit (no save button —
 * "edits commit per control"), matching the spec's "changes recalculate
 * history instantly" behavior once wired to `updateRewardConfig`.
 */
export function LevelArcList({
  levels,
  snapshot,
  editing,
  config,
  onChange,
}: {
  readonly levels: readonly RewardLevelRowView[];
  readonly snapshot: RewardSnapshot;
  readonly editing: boolean;
  readonly config: RewardConfig;
  readonly onChange: (next: RewardConfig) => void;
}) {
  return (
    <div role="list" aria-label="Level curve">
      {levels.map((row, i) => {
        const isCurrent = row.level === snapshot.level;
        const ariaLabel = isCurrent
          ? `Level ${row.level}, ${row.name}, ${row.totalXpRequired} XP, you are here, ${snapshot.xpTotal} XP now`
          : `Level ${row.level}, ${row.name}, ${row.totalXpRequired} XP`;

        return (
          <div
            key={row.level}
            role="listitem"
            aria-label={ariaLabel}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-3) var(--space-3)',
              borderRadius: 'var(--radius-md)',
              background: isCurrent ? 'var(--surface-hover)' : 'transparent',
              border: isCurrent ? '1px solid var(--border-strong)' : '1px solid transparent',
              marginTop: i === 0 ? 0 : 'var(--space-1)',
            }}
          >
            <span
              className="num"
              style={{ width: '24px', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', flex: 'none' }}
            >
              {row.level}
            </span>

            {editing ? (
              <EditableIconTile
                iconId={row.iconId}
                current={isCurrent}
                onChange={(iconId) => onChange(withLevelIdentity(config, row.level, { iconId }))}
              />
            ) : (
              <span
                aria-hidden="true"
                style={{
                  width: '34px',
                  height: '34px',
                  borderRadius: 'var(--radius-sm)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 'none',
                  background: isCurrent ? 'var(--hue-primary-vivid)' : 'var(--surface-hover)',
                }}
              >
                <RewardIcon
                  iconId={row.iconId}
                  size={18}
                  color={isCurrent ? 'var(--text-on-accent)' : 'var(--text-secondary)'}
                />
              </span>
            )}

            {editing ? (
              <input
                value={row.name}
                maxLength={24}
                onChange={(e) => onChange(withLevelIdentity(config, row.level, { name: e.target.value }))}
                style={{
                  flex: 1,
                  minHeight: 'var(--touch-target-min)',
                  padding: '0 var(--space-2)',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-subtle)',
                  background: 'var(--surface-sunken)',
                  color: 'var(--text-primary)',
                  fontSize: 'var(--type-body-size)',
                  fontFamily: 'var(--font-family)',
                }}
              />
            ) : (
              <span style={{ flex: 1, fontSize: 'var(--type-body-size)', fontWeight: 500, color: 'var(--text-primary)' }}>
                {row.name}
                {isCurrent && (
                  <span className="num" style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>
                    {' '}
                    — you are here · {snapshot.xpTotal.toLocaleString()} XP now
                  </span>
                )}
              </span>
            )}

            {editing && row.level > 1 ? (
              <input
                type="number"
                className="num"
                value={row.totalXpRequired}
                min={0}
                onChange={(e) =>
                  onChange(withThreshold(config, row.level, Math.max(0, Number(e.target.value) || 0)))
                }
                style={{
                  width: '84px',
                  minHeight: 'var(--touch-target-min)',
                  padding: '0 var(--space-2)',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-subtle)',
                  background: 'var(--surface-sunken)',
                  color: 'var(--text-primary)',
                  fontSize: 'var(--type-body-size)',
                  fontFamily: 'var(--font-family)',
                  textAlign: 'right',
                }}
              />
            ) : (
              <span className="num" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', flex: 'none' }}>
                {row.totalXpRequired.toLocaleString()} XP
              </span>
            )}
          </div>
        );
      })}
      <p style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginTop: 'var(--space-3)' }}>
        Past {levels[levels.length - 1]?.name ?? 'the top level'}, each level costs the same increment
        again — the curve never runs out.
      </p>
    </div>
  );
}

function EditableIconTile({
  iconId,
  current,
  onChange,
}: {
  readonly iconId: string;
  readonly current: boolean;
  readonly onChange: (iconId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative', flex: 'none' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Change icon, currently ${iconId}`}
        style={{
          width: '34px',
          height: '34px',
          borderRadius: 'var(--radius-sm)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: current ? 'var(--hue-primary-vivid)' : 'var(--surface-hover)',
          border: '1px solid var(--border-strong)',
        }}
      >
        <RewardIcon iconId={iconId} size={18} color={current ? 'var(--text-on-accent)' : 'var(--text-secondary)'} />
      </button>
      {open && (
        <IconPickerPopover
          selected={iconId}
          onSelect={(next) => {
            onChange(next);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function withLevelIdentity(
  config: RewardConfig,
  level: number,
  changes: { readonly name?: string; readonly iconId?: string },
): RewardConfig {
  return {
    ...config,
    levelIdentities: config.levelIdentities.map((identity) =>
      identity.level === level ? { ...identity, ...changes } : identity,
    ),
  };
}

function withThreshold(config: RewardConfig, level: number, nextValue: number): RewardConfig {
  // level N (N >= 2) corresponds to thresholds[N - 2] per LevelCurve's doc
  // comment ("thresholds[i] is the total lifetime XP required to REACH
  // level i + 2"). Level 1 has no threshold (always 0, not editable — the
  // caller guards this by only rendering an editable input for level > 1).
  const index = level - 2;
  const thresholds = config.levelCurve.thresholds.map((t, i) => (i === index ? nextValue : t));
  return { ...config, levelCurve: { thresholds } };
}
